import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface AutomationRun {
  id: number;
  run_date: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SKIPPED';
  users_processed: number;
  new_products_detected: number;
  images_generated: number;
  products_pending_validation: number;
  duration_seconds: number;
  error_message: string;
  started_at: string;
  completed_at: string;
}

export interface AutomationCampaign {
  id: number;
  name: string;
  campaign_id: string;
  status: 'ACTIVE' | 'PAUSED' | 'STOPPED';
  auto_paused: boolean;
  run_hour: number;
  last_run_at: string | null;
  last_successful_run_at: string | null;
  consecutive_failures: number;
  total_runs: number;
  max_inactive_days: number;
  last_used_at: string | null;
  meta_category_id: string;
  category_ids: string;
  created_at: string;
  updated_at: string;
  last_runs: AutomationRun[];
  pending_validations_count: number;
  preview_html_url?: string | null;
  preview_img_url?: string | null;
  generate_html_id?: number | null;
}

export interface AutomationProductValidation {
  id: number;
  campaign: number;
  campaign_name: string;
  product_id: string;
  slot_index: number;
  url_desk: string;
  url_mob: string;
  link: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  validated_by: number | null;
  validated_at: string | null;
  created_at: string;
}

export interface CampaignMetrics {
  total_impressions: number;
  total_clicks: number;
  ctr: number;
  top_products: { product_id: string; slot_index: number; total: number }[];
  daily: { date: string; event_type: string; total: number }[];
}

@Injectable({ providedIn: 'root' })
export class AutomationService {
  private baseUrl = `${environment.backendUrl}/api`;

  constructor(private httpClient: HttpClient) { }

  // --- Campaigns ---

  getCampaigns(): Observable<AutomationCampaign[]> {
    return this.httpClient.get<PaginatedResponse<AutomationCampaign>>(`${this.baseUrl}/automation_campaigns/`)
      .pipe(map(res => res.results));
  }

  getCampaignsByCampaignId(campaignId: string): Observable<AutomationCampaign[]> {
    return this.httpClient.get<PaginatedResponse<AutomationCampaign>>(
      `${this.baseUrl}/automation_campaigns/?campaign_id=${campaignId}`
    ).pipe(map(res => res.results));
  }

  getCampaign(id: number): Observable<AutomationCampaign> {
    return this.httpClient.get<AutomationCampaign>(`${this.baseUrl}/automation_campaigns/${id}/`);
  }

  updateCampaign(id: number, data: Partial<AutomationCampaign>): Observable<AutomationCampaign> {
    return this.httpClient.patch<AutomationCampaign>(`${this.baseUrl}/automation_campaigns/${id}/`, data);
  }

  deleteCampaign(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/automation_campaigns/${id}/`);
  }

  pauseCampaign(id: number): Observable<{ status: string }> {
    return this.httpClient.post<{ status: string }>(`${this.baseUrl}/automation_campaigns/${id}/pause/`, {});
  }

  resumeCampaign(id: number): Observable<{ status: string }> {
    return this.httpClient.post<{ status: string }>(`${this.baseUrl}/automation_campaigns/${id}/resume/`, {});
  }

  stopCampaign(id: number): Observable<{ status: string }> {
    return this.httpClient.post<{ status: string }>(`${this.baseUrl}/automation_campaigns/${id}/stop/`, {});
  }

  getCampaignRuns(id: number): Observable<AutomationRun[]> {
    return this.httpClient.get<AutomationRun[]>(`${this.baseUrl}/automation_campaigns/${id}/runs/`);
  }

  getCampaignMetrics(id: number, days = 30): Observable<CampaignMetrics> {
    return this.httpClient.get<CampaignMetrics>(`${this.baseUrl}/automation_campaigns/${id}/metrics/?days=${days}`);
  }

  // --- Validations ---

  getPendingValidations(): Observable<AutomationProductValidation[]> {
    return this.httpClient.get<PaginatedResponse<AutomationProductValidation>>(
      `${this.baseUrl}/automation_validations/?status=PENDING`
    ).pipe(map(res => res.results));
  }

  getAllValidations(): Observable<AutomationProductValidation[]> {
    return this.httpClient.get<PaginatedResponse<AutomationProductValidation>>(`${this.baseUrl}/automation_validations/`)
      .pipe(map(res => res.results));
  }

  approveValidation(id: number): Observable<{ status: string }> {
    return this.httpClient.post<{ status: string }>(`${this.baseUrl}/automation_validations/${id}/approve/`, {});
  }

  rejectValidation(id: number): Observable<{ status: string }> {
    return this.httpClient.post<{ status: string }>(`${this.baseUrl}/automation_validations/${id}/reject/`, {});
  }

  bulkApprove(ids: number[]): Observable<{ approved: number }> {
    return this.httpClient.post<{ approved: number }>(`${this.baseUrl}/automation_validations/bulk_approve/`, { ids });
  }
}
